import React, { useEffect, useMemo, useState, useContext } from 'react'

import LoadingContext from '../../context/Loading/Loading'

import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Tab from 'react-bootstrap/Tab'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'

import {
  getReaders,
  getLocations,
  postLocation,
  getTeamSettings,
  assignReader,
  createOrEditReader,
} from '../../utilities/api'
import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import ReadersTable from './ReadersTable'
import LocationsTable from './LocationsTable'
import AssignmentsTable from './AssignmentsTable'

const EMPTY_LOCATION = {
  id: '',
  display_name: '',
}

const EMPTY_READER = {
  id: '',
  registration_code: '',
  label: '',
  location: '',
}

const getStripeAccountLabel = (isDestinationAccount, connectedAccountId) => {
  if (isDestinationAccount) {
    return 'BlockTickets platform account'
  }

  return connectedAccountId
    ? `Connected account (${connectedAccountId})`
    : 'Connected account'
}

export default function TerminalsWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.MANAGE_TERMINALS
    )

  const isProd = process.env.REACT_APP_STAGE === 'production'
  const isDestinationAccount = organization?.destinationAccount === true
  const connectedAccountId = isProd
    ? organization?.connected_account_id
    : organization?.test_connected_account_id ||
      organization?.connected_account_id
  const stripeAccountLabel = getStripeAccountLabel(
    isDestinationAccount,
    connectedAccountId
  )
  const stripeAccountScope = isDestinationAccount ? 'platform' : 'connected'

  const [organizationUUID, setOrganizationUUID] = useState()
  const [readers, setReaders] = useState([])
  const [locations, setLocations] = useState([])
  const [key, setKey] = useState('locations')
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showReaderModal, setShowReaderModal] = useState(false)
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [assignments, setAssignments] = useState([])
  const [location, setLocation] = useState(EMPTY_LOCATION)
  const [reader, setReader] = useState(EMPTY_READER)
  const [assignment, setAssignment] = useState({
    profile_id: '',
    pos_terminal_id: '',
  })
  const [submitError, setSubmitError] = useState('')
  const [readerValidationAttempted, setReaderValidationAttempted] =
    useState(false)

  const activeOrganizationUUID = organizationUUID ?? organization?.uuid
  const isEditingReader = Boolean(reader.id)

  const readerFieldErrors = useMemo(() => {
    const errors = {}

    if (!reader.label?.trim()) {
      errors.label = 'Reader name is required.'
    }

    if (!reader.location) {
      errors.location = 'Select a location.'
    }

    if (!isEditingReader && !reader.registration_code?.trim()) {
      errors.registration_code = 'Pairing code is required.'
    }

    return errors
  }, [isEditingReader, reader])

  const isReaderFormValid = Object.keys(readerFieldErrors).length === 0

  const terminalConfig = useMemo(
    () => ({
      destinationAccount: isDestinationAccount,
      stripeAccountScope,
      stripeAccountLabel,
      connectedAccountId,
    }),
    [
      connectedAccountId,
      isDestinationAccount,
      stripeAccountLabel,
      stripeAccountScope,
    ]
  )

  const handleCloseLocationModal = () => {
    setShowLocationModal(false)
    setSubmitError('')
  }

  const handleShowLocationModal = (existingLocation) => {
    setSubmitError('')
    setLocation(
      existingLocation
        ? {
            id: existingLocation.location_id,
            display_name: existingLocation.displayName,
          }
        : EMPTY_LOCATION
    )
    setShowLocationModal(true)
  }

  const handleCloseReaderModal = () => {
    setShowReaderModal(false)
    setSubmitError('')
    setReaderValidationAttempted(false)
  }

  const handleShowReaderModal = (existingReader) => {
    setSubmitError('')
    setReaderValidationAttempted(false)
    setReader(
      existingReader
        ? {
            id: existingReader.stripeReaderId,
            registration_code: existingReader.registrationCode || '',
            label: existingReader.label,
            location: existingReader.location,
          }
        : EMPTY_READER
    )
    setShowReaderModal(true)
  }

  const handleCloseAssignmentModal = () => {
    setShowAssignmentModal(false)
    setSubmitError('')
    setAssignment({
      profile_id: '',
      pos_terminal_id: '',
    })
  }
  const handleShowAssignmentModal = (member) => {
    setSubmitError('')
    setAssignment({
      profile_id: member.profile.id,
      pos_terminal_id: '',
    })
    setShowAssignmentModal(true)
  }

  const loadReaders = (orgUUID) => {
    getReaders(orgUUID)
      .then((res) => {
        setReaders(res.data)
        loadLocations(orgUUID)
        setOrganizationUUID(orgUUID)
        hideLoading()
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }

  const loadLocations = (orgUUID) => {
    getLocations(orgUUID)
      .then((res) => {
        setLocations(res.data)
        loadTeam(orgUUID)
      })
      .catch((err) => console.error(err))
  }

  const loadTeam = (orgUUID) => {
    getTeamSettings(orgUUID)
      .then((res) => {
        setAssignments(res.data?.members)
      })
      .catch((err) => console.error(err))
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setLocation((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleReaderChange = (e) => {
    const { name, value } = e.target
    setReader((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleAssignmentChange = (e) => {
    const { name, value } = e.target
    setAssignment((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSumbit = () => {
    setSubmitError('')
    postLocation({
      location: {
        ...location,
        organizationUUID: activeOrganizationUUID,
        destinationAccount: isDestinationAccount,
      },
    })
      .then(() => {
        loadLocations(organization.uuid)
        handleCloseLocationModal()
      })
      .catch((err) => {
        console.error(err)
        setSubmitError(
          err?.response?.data?.error?.message ||
            'Unable to save this location in Stripe. Please try again.'
        )
      })
  }

  const handleReaderSumbit = () => {
    setSubmitError('')
    setReaderValidationAttempted(true)

    if (!isReaderFormValid) {
      return
    }

    createOrEditReader({
      ...reader,
      organizationUUID: activeOrganizationUUID,
      destinationAccount: isDestinationAccount,
    })
      .then(() => {
        loadReaders(organization.uuid)
        handleCloseReaderModal()
      })
      .catch((err) => {
        console.error(err)
        setSubmitError(
          err?.response?.data?.error?.message ||
            'Unable to save this reader in Stripe. Please try again.'
        )
      })
  }

  const handleAssignmentSumbit = () => {
    if (!assignment.pos_terminal_id) {
      setSubmitError('Please select a reader.')
      return
    }

    setSubmitError('')
    assignReader({
      ...assignment,
      organizationUUID: activeOrganizationUUID,
    })
      .then(() => {
        handleCloseAssignmentModal()
        loadTeam(organization.uuid)
      })
      .catch((err) => {
        console.error(err)
        setSubmitError(
          err?.response?.data?.error?.message ||
            'Unable to assign this reader. Please try again.'
        )
      })
  }

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!organization?.uuid) return

    showLoading()
    loadReaders(organization.uuid)
  }, [orgPermissionsReady, hasPermission, organization?.uuid])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <section className="wrapper">
        <header className="section-header">
          <div className="section-heading section-heading--secondary">
            <h1>Terminals</h1>
          </div>
          <p className="section-header-desc">
            Manage your payment terminals associated with your account.
          </p>
        </header>
        <Alert variant="info" className="mb-3">
          {isDestinationAccount ? (
            <>
              This organization uses destination charges. New locations and
              readers are registered on the{' '}
              <strong>{stripeAccountLabel}</strong>. Payments are collected on
              the platform and transferred to the connected account.
            </>
          ) : (
            <>
              This organization uses direct charges. New locations and readers
              are registered on the <strong>{stripeAccountLabel}</strong>.
            </>
          )}
        </Alert>
        <Card body className="card--sm">
          <Tab.Container
            defaultActiveKey={key}
            activeKey={key}
            onSelect={(k) => setKey(k)}
          >
            <div className="d-flex mb-2">
              <Nav as="ul" variant="pills" justify>
                <Nav.Item as="li">
                  <Nav.Link as="button" eventKey="locations">
                    Locations
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as="button" eventKey="readers">
                    Readers
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as="button" eventKey="assignments">
                    Assignments
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            <Tab.Content>
              <Tab.Pane unmountOnExit={true} eventKey="locations">
                <LocationsTable
                  locations={locations}
                  terminalConfig={terminalConfig}
                  handleShowLocationModal={handleShowLocationModal}
                />
              </Tab.Pane>
              <Tab.Pane unmountOnExit={true} eventKey="readers">
                <ReadersTable
                  readers={readers}
                  locations={locations}
                  terminalConfig={terminalConfig}
                  handleShowReaderModal={handleShowReaderModal}
                />
              </Tab.Pane>
              <Tab.Pane unmountOnExit={true} eventKey="assignments">
                <AssignmentsTable
                  assignments={assignments}
                  handleShowAssignmentModal={handleShowAssignmentModal}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card>
      </section>

      <Modal show={showLocationModal} onHide={handleCloseLocationModal}>
        <Modal.Header closeButton>
          <Modal.Title>Location Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="secondary" className="mb-3">
            This location will be created in Stripe on the{' '}
            <strong>{stripeAccountLabel}</strong>.
          </Alert>
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          ) : null}
          <Form>
            <Form.Group className="mb-3" controlId="display_name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name for location"
                name="display_name"
                value={location.display_name}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleCloseLocationModal}>
            Close
          </Button>
          <Button variant="primary" onClick={() => handleSumbit()}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showReaderModal} onHide={handleCloseReaderModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditingReader ? 'Edit Reader' : 'Add Reader'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="secondary" className="mb-3">
            {isEditingReader ? (
              <>
                Update this reader&apos;s label. Pairing codes cannot be changed
                after registration.
              </>
            ) : (
              <>
                This reader will be registered in Stripe on the{' '}
                <strong>{stripeAccountLabel}</strong> using the pairing code
                shown on the terminal.
              </>
            )}
          </Alert>
          {!isProd && !isEditingReader ? (
            <Alert variant="warning" className="mb-3">
              Non-production environments register a simulated reader. The
              pairing code is still required in this form, but Stripe will use a
              test reader instead.
            </Alert>
          ) : null}
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          ) : null}
          <Form noValidate onSubmit={(e) => e.preventDefault()}>
            <Form.Group className="mb-3" controlId="label">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name for reader"
                name="label"
                value={reader.label}
                onChange={handleReaderChange}
                isInvalid={
                  readerValidationAttempted && Boolean(readerFieldErrors.label)
                }
                required
              />
              <Form.Control.Feedback type="invalid">
                {readerFieldErrors.label}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="registration_code">
              <Form.Label>Pairing Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Reader pairing code"
                name="registration_code"
                value={reader.registration_code}
                onChange={handleReaderChange}
                disabled={isEditingReader}
                readOnly={isEditingReader}
                isInvalid={
                  readerValidationAttempted &&
                  Boolean(readerFieldErrors.registration_code)
                }
                required={!isEditingReader}
              />
              {isEditingReader ? (
                <Form.Text muted>
                  Pairing is only used when a reader is first registered.
                </Form.Text>
              ) : null}
              <Form.Control.Feedback type="invalid">
                {readerFieldErrors.registration_code}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="location">
              <Form.Label>Location</Form.Label>
              <Form.Select
                aria-label="location"
                required
                value={reader.location}
                onChange={handleReaderChange}
                name="location"
                isInvalid={
                  readerValidationAttempted &&
                  Boolean(readerFieldErrors.location)
                }
              >
                <option value="">Select a location</option>
                {locations &&
                  locations.map((option) => (
                    <option key={option.location_id} value={option.location_id}>
                      {option.displayName}
                    </option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {readerFieldErrors.location}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleCloseReaderModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => handleReaderSumbit()}
            disabled={readerValidationAttempted && !isReaderFormValid}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAssignmentModal} onHide={handleCloseAssignmentModal}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Reader</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3" controlId="pos_terminal_id">
              <Form.Label>Reader</Form.Label>
              <Form.Select
                aria-label="reader"
                required
                value={assignment.pos_terminal_id}
                onChange={handleAssignmentChange}
                name="pos_terminal_id"
              >
                <option value="">Select a reader</option>
                {readers &&
                  readers.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleCloseAssignmentModal}
          >
            Close
          </Button>
          <Button variant="primary" onClick={() => handleAssignmentSumbit()}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
